import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/context/LanguageContext";
import { companyConfig, getSiteContent } from "@/data/siteContent";
import { SectionTitle } from "@/components/common/SectionTitle";

export default function HomePage() {
  const { language, isArabic } = useLanguage();
  const content = getSiteContent(language);
  const serviceCatalogRoutes = {
    cleaning: "/services/cleaning-catalog",
    hospitality: "/services/hospitality-catalog",
  };

  return (
    <div data-testid="home-page">
      <section className="container mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24 lg:px-8" data-testid="home-hero-section">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Badge className="mb-5 bg-secondary/15 text-secondary" data-testid="hero-badge">
              {content.home.badge}
            </Badge>
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {content.home.title}
            </h1>
            <p className="mt-4 text-sm font-semibold text-secondary" data-testid="hero-about-snippet-title">
              {content.home.aboutSnippetTitle}
            </p>
            <p className="mt-5 max-w-xl text-base text-slate-600 md:text-lg" data-testid="hero-subtitle">
              {content.home.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild className="h-11 cursor-pointer whitespace-nowrap bg-primary px-6 text-sm md:h-12 md:text-base" data-testid="hero-primary-cta-button">
                <Link to="/contact">
                  {content.home.ctaPrimary} <ArrowRight className={`h-4 w-4 ${isArabic ? "rotate-180" : ""}`} />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-11 cursor-pointer whitespace-nowrap border-slate-300 px-6 text-sm md:h-12 md:text-base" data-testid="hero-secondary-cta-button">
                <Link to="/services">{content.home.ctaSecondary}</Link>
              </Button>
            </div>
            <div className="mt-5 overflow-hidden rounded-[28px] bg-[#0b244f] shadow-[0_25px_45px_rgba(15,23,42,0.28)]" data-testid="hero-vision-2030-banner">
              <div className="relative p-2 md:p-3" data-testid="hero-vision-2030-banner-image-frame">
                <img
                  src={content.home.visionBannerImage}
                  alt={content.home.visionBannerTitle}
                  className="h-auto w-full rounded-[22px] object-contain"
                  data-testid="hero-vision-2030-banner-image"
                />
                <div className="pointer-events-none absolute inset-x-2 bottom-2 rounded-b-[22px] bg-gradient-to-t from-[#081a34]/90 to-transparent p-3 text-right md:inset-x-3 md:bottom-3 md:p-5" data-testid="hero-vision-2030-banner-header">
                  <h3 className="text-xl font-extrabold text-white md:text-3xl" data-testid="hero-vision-2030-banner-title">
                    {content.home.visionBannerTitle}
                  </h3>
                  <p className="mt-2 text-sm font-medium text-white/95 md:text-lg" data-testid="hero-vision-2030-banner-text">
                    {content.home.visionBannerText}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-7 grid grid-cols-3 gap-3" data-testid="hero-stats-grid">
              {content.home.stats.map((stat, index) => (
                <Card key={stat.label} className="glass-panel" data-testid={`hero-stat-card-${index}`}>
                  <CardContent className="p-4 text-center">
                    <p className="text-xl font-bold text-primary" data-testid={`hero-stat-value-${index}`}>
                      {stat.value}
                    </p>
                    <p className="text-xs text-slate-600 md:text-sm" data-testid={`hero-stat-label-${index}`}>
                      {stat.label}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: isArabic ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 hero-image-shadow">
              <img
                src={content.home.heroImage}
                alt="Root Trade supply operations"
                className="aspect-[5/4] w-full rounded-2xl object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24" data-testid="home-services-preview-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          <SectionTitle
            badge={isArabic ? "خدماتنا" : "Our Services"}
            title={isArabic ? "حلول توريد تلائم متطلبات منشأتكم" : "Supply solutions tailored to your facility"}
            description={isArabic ? "نغطي احتياجات النظافة والضيافة واللوازم التشغيلية بمرونة وجودة." : "We cover hygiene, hospitality, and operational requirements with quality and flexibility."}
            testIdPrefix="home-services"
          />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="home-services-cards-grid">
            {content.services.slice(0, 4).map((service, index) => {
              const Icon = service.icon;
              const serviceTitle = isArabic ? service.titleAr : service.titleEn;
              const serviceDesc = isArabic ? service.descriptionAr : service.descriptionEn;
              const serviceRoute = serviceCatalogRoutes[service.key];
              const isCatalogService = Boolean(serviceRoute);

              return (
                <motion.div
                  key={service.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.12 }}
                >
                  {isCatalogService ? (
                    <Link to={serviceRoute} className="block cursor-pointer" data-testid={`home-service-${service.key}-card-link`}>
                      <Card className="h-full overflow-hidden border-slate-200 shadow-sm ring-2 ring-secondary/30 transition-shadow duration-300 hover:shadow-xl" data-testid={`home-service-card-${service.key}`}>
                        <img
                          src={service.image}
                          alt={serviceTitle}
                          className="h-48 w-full object-cover"
                          data-testid={`home-service-image-${service.key}`}
                        />
                        <CardContent className="space-y-3 p-5">
                          <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10 text-secondary" data-testid={`home-service-icon-wrap-${service.key}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <h3 className="text-xl font-bold text-slate-900" data-testid={`home-service-title-${service.key}`}>
                            {serviceTitle}
                          </h3>
                          <p className="text-sm text-slate-600" data-testid={`home-service-description-${service.key}`}>
                            {serviceDesc}
                          </p>
                          <span
                            className="inline-flex h-10 items-center justify-center rounded-md bg-secondary px-4 text-sm font-semibold text-white"
                            data-testid={`home-service-${service.key}-open-catalog-button`}
                          >
                            {isArabic ? "عرض محتوى الملف" : "Open PDF Content"}
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    <Card className="h-full overflow-hidden border-slate-200 shadow-sm transition-shadow duration-300 hover:shadow-xl" data-testid={`home-service-card-${service.key}`}>
                      <img
                        src={service.image}
                        alt={serviceTitle}
                        className="h-48 w-full object-cover"
                        data-testid={`home-service-image-${service.key}`}
                      />
                      <CardContent className="space-y-3 p-5">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10 text-secondary" data-testid={`home-service-icon-wrap-${service.key}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900" data-testid={`home-service-title-${service.key}`}>
                          {serviceTitle}
                        </h3>
                        <p className="text-sm text-slate-600" data-testid={`home-service-description-${service.key}`}>
                          {serviceDesc}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="home-trust-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          <div className="glass-panel rounded-3xl p-6 md:p-10">
            <SectionTitle
              badge={isArabic ? "لماذا نحن" : "Why Root Trade"}
              title={isArabic ? "جودة ثابتة وسرعة توريد تبني الثقة" : "Consistent quality and responsive supply that builds trust"}
              description={isArabic ? "نقدم تجربة توريد عملية من الاستجابة السريعة وحتى المتابعة بعد التسليم." : "From quick response to post-delivery follow-up, we provide an efficient procurement experience."}
              testIdPrefix="home-trust"
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="home-trust-points-grid">
              {[0, 1, 2].map((index) => (
                <div
                  key={`trust-point-${index}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5"
                  data-testid={`home-trust-point-card-${index}`}
                >
                  <CheckCircle2 className="mb-3 h-5 w-5 text-secondary" />
                  <p className="text-sm text-slate-700" data-testid={`home-trust-point-text-${index}`}>
                    {index === 0
                      ? isArabic
                        ? "التزام بمعايير الجودة والسلامة في كل منتج."
                        : "Strict quality and safety standards across all products."
                      : index === 1
                        ? isArabic
                          ? "إدارة مرنة لطلبات الشركات الكبيرة والمتكررة."
                          : "Flexible handling for recurring and high-volume business requests."
                        : isArabic
                          ? "دعم مباشر وسريع عبر الهاتف والواتساب والبريد."
                          : "Direct and fast support via phone, WhatsApp, and email."}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="home-identity-blocks-grid">
              {content.homeIdentityBlocks.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.key}
                    className="rounded-2xl border border-slate-200 bg-white p-5"
                    data-testid={`home-identity-card-${item.key}`}
                  >
                    <div
                      className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10 text-secondary"
                      data-testid={`home-identity-icon-${item.key}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-testid={`home-identity-title-${item.key}`}>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-700" data-testid={`home-identity-text-${item.key}`}>
                      {item.text}
                    </p>
                    {item.points ? (
                      <ul className="mt-3 space-y-1" data-testid={`home-identity-points-${item.key}`}>
                        {item.points.map((point, index) => (
                          <li
                            key={`${item.key}-point-${index}`}
                            className="text-xs text-slate-600 md:text-sm"
                            data-testid={`home-identity-point-${item.key}-${index}`}
                          >
                            • {point}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild className="cursor-pointer whitespace-nowrap" data-testid="home-final-contact-button">
                <Link to="/contact">{isArabic ? "تواصل معنا الآن" : "Contact Us Now"}</Link>
              </Button>
              <a
                href={companyConfig.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center justify-center rounded-md border border-secondary px-4 text-sm font-semibold text-secondary transition-colors duration-300 hover:bg-secondary hover:text-white"
                data-testid="home-whatsapp-link-button"
              >
                {isArabic ? "تواصل واتساب" : "WhatsApp Chat"}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
